import { CommonModule } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, inject, Injectable, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { CustomMatPaginatorIntl } from '@common/util/mat-pagination-provider';
import { ProductProvider } from '@common/util/products';
import { NamingModule } from '@shared/naming/naming.module';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';
import {
  PH_AUTH_SERVICE_CONFIG,
  PhAuthModule,
  PhAuthService,
  PhAuthServiceConfig,
} from '../common/ph-auth';
import {
  PhConfigLoaderModel,
  PhConfigLoaderModule,
  PhConfigLoaderService,
} from '../common/ph-config-loader';
import { APOLLO_CONFIG } from './apollo.config';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GraphQlGuard } from './graphql.guard';
import { AppSwitcherModule } from './sidebar/app-switcher/app-switcher.module';
import { SidebarModule } from './sidebar/sidebar.module';

@Injectable()
export class PhConfigLoaderFactory {
  constructor(private http: HttpClient) {}

  cb = (config: PhConfigLoaderModel): Observable<PhConfigLoaderModel> =>
    this.http
      .get<{
        id: number;
        auth_org_id: string;
        locale: string;
      }>(config.CA_REST_API_URL + 'customers/id')
      .pipe(
        map(customer => customer),
        catchError(() => of({ id: 'disabled', auth_org_id: null, locale: null })),
        map(customer => ({
          ...config,
          CUSTOMER_ID: customer.id,
          LOCALE: customer.locale,
          AUTH_ORG_ID: customer.auth_org_id,
        }))
      );
}
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    AppSwitcherModule,
    CommonModule,
    PhAuthModule,
    PhConfigLoaderModule.forRoot(PhConfigLoaderFactory),
    MatSidenavModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatToolbarModule,
    MatTableModule,
    MatSortModule,
    BrowserAnimationsModule,
    MatPaginatorModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    SidebarModule,
    NamingModule.forRoot(),
  ],
  providers: [
    APOLLO_CONFIG,
    GraphQlGuard,
    provideApollo(() => {
      const configLoader = inject(PhConfigLoaderService);
      const phAuthSrv = inject(PhAuthService);
      const httpLink = inject(HttpLink);

      const authContext = setContext(async () => ({
        headers: {
          Authorization: `Bearer ${phAuthSrv.token}`,
        },
      }));

      const apiGatewayHTTPLink = httpLink.create({
        uri: configLoader.getConfig('API_GATEWAY_URL'),
      });

      return {
        link: ApolloLink.from([authContext, apiGatewayHTTPLink]),
        cache: new InMemoryCache(),
      };
    }),
    {
      provide: APP_INITIALIZER,
      deps: [PhConfigLoaderService, AuthClientConfig],
      useFactory: (ac: PhConfigLoaderService) => () => ac.load(),
      multi: true,
    },
    {
      provide: PH_AUTH_SERVICE_CONFIG,
      useFactory: phAuthServiceFactory,
      deps: [PhConfigLoaderService],
    },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    { provide: MAT_PAGINATOR_DEFAULT_OPTIONS, useValue: { formFieldAppearance: 'fill' } },
    provideHttpClient(withInterceptorsFromDi()),
    ProductProvider,
  ],
})
export class AppModule {}

function phAuthServiceFactory(configSrv: PhConfigLoaderService): PhAuthServiceConfig {
  return combineLatest([
    configSrv.getConfig$('CA_REST_API_URL'),
    configSrv.getConfig$('AUTH_DOMAIN'),
    configSrv.getConfig$('AUTH_AUDIENCE'),
    configSrv.getConfig$('AUTH_ORG_ID'),
    configSrv.getConfig$('REFRESH_TOKEN_IN_ADVANCE_SEC'),
    configSrv.getConfig$('AUTH_LOGOUT_CALLBACK_URL'),
    configSrv.getConfig$('LOCALE'),
  ]).pipe(
    map(
      ([
        CA_REST_API_URL,
        AUTH_DOMAIN,
        AUTH_AUDIENCE,
        AUTH_ORG_ID,
        REFRESH_TOKEN_IN_ADVANCE_SEC,
        AUTH_LOGOUT_CALLBACK_URL,
        LOCALE,
      ]) => ({
        CA_REST_API_URL,
        AUTH_DOMAIN,
        AUTH_AUDIENCE,
        AUTH_ORG_ID,
        REFRESH_TOKEN_IN_ADVANCE_SEC,
        AUTH_LOGOUT_CALLBACK_URL,
        LOCALE,
      })
    )
  );
}
